import {
  ButtonList,
  FlexBox,
  IconButton,
  InputText,
  Text,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { ampli } from 'lib-shared-tracking';
import { KeyboardEvent, useState } from 'react';

import { AppLink } from 'components/appLink';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './topBarSearchField.module.scss';
import { isMaybeConversationId } from './utils/isMaybeConversationId';

type TopBarSearchFieldProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export const TopBarSearchField = ({
  isOpen,
  onOpenChange,
}: TopBarSearchFieldProps): JSX.Element | null => {
  const navigate = useAppNavigate();
  const flags = useFeatureFlag(['top-bar-search-quick-actions']);

  const [value, setValue] = useState('');

  const isMaybeCsid =
    flags.topBarSearchQuickActions && isMaybeConversationId(value);

  const handleOnSubmit = () => {
    if (value) {
      ampli.searchFieldSubmitted({
        location: 'topbar',
        isEmpty: false,
        query: value,
      });

      navigate({
        path: '/search/:scope',
        params: {
          path: { scope: 'conversations' },
          search: { q: value },
        },
      });

      onOpenChange?.(!isOpen);
    }
  };

  const resetSearchField = () => {
    setValue('');
    onOpenChange?.(false);
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleOnSubmit();
    }

    if (event.key === 'Escape') {
      resetSearchField();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Popover.Root open={isMaybeCsid}>
      <Popover.Trigger asChild>
        <InputText
          className={styles.root}
          type="search"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Search..."
          onFocus={() => ampli.searchFieldFocused({ location: 'topbar' })}
          onBlur={(e) => {
            if (
              e.relatedTarget &&
              !e.currentTarget?.contains(e.relatedTarget as Element)
            ) {
              return;
            }

            ampli.searchFieldBlurred({ location: 'topbar' });
            resetSearchField();
          }}
          onKeyDown={handleOnKeyDown}
          data-testid="top-bar-search-input-field"
          suffix={{
            type: 'custom',
            value: (
              <FlexBox alignItems="center" padding="0 100" gap="100">
                <IconButton
                  className={styles.inputButton}
                  variant="tertiary"
                  size="normal"
                  icon={'close'}
                  onClick={() => {
                    setValue('');
                    onOpenChange?.(!isOpen);
                  }}
                  tabIndex={-1}
                />
              </FlexBox>
            ),
            noWrap: true,
          }}
        />
      </Popover.Trigger>

      <Popover.Content
        className={styles.popover}
        side="bottom"
        align="start"
        sideOffset={parseInt(sizes.spacer.values[200])}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ButtonList>
          <AppLink
            path="/conversation/:csid"
            params={{
              path: {
                csid: value.replace('#', ''),
              },
            }}
            tabIndex={-1}
          >
            <ButtonList.Item
              icon="conversations"
              onClick={() => {
                ampli.topbarSearchQuickActionClicked({
                  name: 'openConversation',
                });
                resetSearchField();
              }}
            >
              <span className={styles.buttonListItemContent}>
                <span>Open conversation</span>

                <Text.Paragraph as="span" variant="4" subdued>
                  Go to conversation #{value.replace('#', '')}
                </Text.Paragraph>
              </span>
            </ButtonList.Item>
          </AppLink>

          <ButtonList.Divider />

          <AppLink
            path="/search/:scope"
            params={{
              path: {
                scope: 'conversations',
              },
              search: {
                q: value,
              },
            }}
            tabIndex={-1}
          >
            <ButtonList.Item
              icon="search"
              onClick={() => {
                ampli.topbarSearchQuickActionClicked({
                  name: 'advancedSearch',
                });
                resetSearchField();
              }}
            >
              <span className={styles.buttonListItemContent}>
                <span>Search</span>

                <Text.Paragraph as="span" variant="4" subdued>
                  Find conversations matching &quot;{value}&quot;
                </Text.Paragraph>
              </span>
            </ButtonList.Item>
          </AppLink>
        </ButtonList>
      </Popover.Content>
    </Popover.Root>
  );
};
